/**
 * Title + description for routes whose React page component is shared across
 * several SEO paths (DirectorHome serves 5 paths, DownloadApp serves 2).
 *
 * Why this exists: the server renders per-route SEO from ROUTE_SEO in
 * server/_core/seo.ts, but a shared component calls useSEO() with one
 * hardcoded title, which overwrites the server's <title>/description on
 * hydration. Crawlers that don't run JS keep the correct SSR head, but Google
 * renders JS — so it saw every one of these routes carrying the shared
 * component's title (e.g. /ai-music-video-generator presenting the homepage
 * title), which reads as duplicate titles across the cluster.
 *
 * Both sides import this map, so the hydrated <head> matches the SSR <head>
 * and the two can't drift. Server: ROUTE_SEO spreads these values. Client:
 * DirectorHome / DownloadApp look up the current path and pass the result to
 * useSEO.
 *
 * Only paths where the shared component would otherwise clobber a distinct
 * server title belong here. "/" is intentionally absent: its SSR title already
 * equals DirectorHome's default, so there is nothing to correct.
 */
import {
  DANCE_DIRECTOR_PATH,
  DANCE_SEO_HEADS,
  getDanceSeoHead,
} from "./danceSeoHead.js";

export type SharedComponentRouteSeo = {
  title: string;
  description: string;
};

export const SHARED_COMPONENT_ROUTE_SEO: Record<
  string,
  SharedComponentRouteSeo
> = {
  "/ai-music-video-generator": {
    // ≤60 chars so the full promise survives Google's ~600px title cut (the
    // old 66-char title truncated at "into a …"). Brand suffix dropped on
    // purpose: Google renders the site name above the title anyway.
    title: "AI Music Video Generator — Turn Any Song into a Video",
    description:
      "Solmi's AI music video generator turns any song, Suno track, or MP3 into a finished, lip-synced music video. Beat-synced visuals, auto lyrics, free to start.",
  },
  "/free-ai-music-video-generator": {
    title: "Free AI Music Video Generator — Try It Free, No Card | Solmi",
    description:
      "Free AI music video generator: turn any song or Suno link into a storyboarded, lip-synced music video. Free 20-second preview, no card. 1080p on paid plans.",
  },
  "/suno-to-video": {
    title: "Suno to Video — Turn Suno Songs into Music Videos | Solmi",
    description:
      "Paste your Suno share link and Solmi turns the song into a finished music video with auto-detected lyrics and beat-synced visuals. Free to start.",
  },
  "/ai-music-video-director": {
    title: "AI Music Video Director — Storyboard and Render | Solmi",
    description:
      "Solmi directs, storyboards, and renders music videos: it reads your song, writes a brief, builds a shot-by-shot storyboard with a consistent cast, then renders.",
  },
  "/ai-music-video-workflow": {
    title: "AI Music Video Workflow — Song to Video in One Tool | Solmi",
    description:
      "Solmi handles the full music video workflow: song analysis, creative brief, storyboard, character references, rendering, lip-sync, and export. Free to start.",
  },
  "/how-to-make-ai-music-video": {
    title: "How to Make an AI Music Video — Step-by-Step (2026)",
    description:
      "Learn how to make an AI music video from any song: add a track or Suno link, approve the storyboard, and export a lip-synced video for YouTube and TikTok. Free.",
  },
  "/ai-dance-video-generator": {
    // DirectorHome in its dance variant. ≤60 chars, brand suffix dropped
    // (Google renders the site name above the title). English here; the
    // other 13 languages come from shared/danceSeoHead.ts (see
    // getSharedComponentRouteSeo below).
    title: DANCE_SEO_HEADS.en.director.title,
    description: DANCE_SEO_HEADS.en.director.description,
  },
  "/ai-music-video-app": {
    title: "AI Music Video App for iOS & Android — Free to Start",
    description:
      "Solmi is the AI music video maker with native iOS and Android apps plus a full web app. Upload a song or paste a Suno link to render a lip-synced music video.",
  },
};

/**
 * Look up the SEO for a path that may carry a language prefix (/es/foo).
 * `languages` is passed in by the caller so this module stays free of both the
 * server's and the client's language-list imports.
 */
export function getSharedComponentRouteSeo(
  pathname: string,
  languages: readonly string[],
): SharedComponentRouteSeo | null {
  const withoutQuery = pathname.split("?")[0]?.split("#")[0] ?? "/";
  const parts = withoutQuery.split("/").filter(Boolean);
  const hasLang = languages.includes(parts[0] ?? "");
  const basePath = hasLang
    ? `/${parts.slice(1).join("/")}`
    : `/${parts.join("/")}`;
  // The dance page is the one shared-component route with a localized head:
  // the server renders it per language (server/_core/seo.ts
  // buildDanceDirectorSeo), so hydration has to land on the same strings.
  if (hasLang && basePath === DANCE_DIRECTOR_PATH) {
    const { title, description } = getDanceSeoHead(parts[0]!).director;
    return { title, description };
  }
  return SHARED_COMPONENT_ROUTE_SEO[basePath] ?? null;
}
